/**
 * Par área principal × subárea: orçamento (`area`, `sub_area`) vs realizado
 * (`area_tratado`, `sub_area_tratado`).
 */

export type AreaPair = { area: string; subArea: string };

const SEP = "\u0001";

function trimPart(s: string | null | undefined): string {
  return (s ?? "").trim();
}

export function rowToAreaPair(area: string | null | undefined, subArea: string | null | undefined): AreaPair {
  return {
    area: trimPart(area),
    subArea: trimPart(subArea),
  };
}

export function areaPairKeyExact(p: AreaPair): string {
  return `${p.area}${SEP}${p.subArea}`;
}

function normalizeToken(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function areaPairKeyNormalized(p: AreaPair): string {
  return `${normalizeToken(p.area)}|${normalizeToken(p.subArea)}`;
}

export function parseAreaExactKey(key: string): AreaPair {
  const [area, subArea] = key.split(SEP);
  return { area: area ?? "", subArea: subArea ?? "" };
}

export type AreaPairCount = AreaPair & { count: number };

export type AreaSpellingDivergence = {
  normalizedKey: string;
  orcamentoVariants: AreaPairCount[];
  realizadoVariants: AreaPairCount[];
};

export type AreaAuditResult = {
  orcamentoDistinct: number;
  realizadoDistinct: number;
  bothExact: number;
  onlyOrcamento: AreaPairCount[];
  onlyRealizado: AreaPairCount[];
  spellingDivergences: AreaSpellingDivergence[];
  orcamentoRowsAnalisadas: number;
  realizadoRowsAnalisadas: number;
  orcamentoSemPar: number;
  realizadoSemPar: number;
};

function countByKey(rows: AreaPair[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const p of rows) {
    const k = areaPairKeyExact(p);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

function pairIsEmpty(p: AreaPair): boolean {
  return !p.area && !p.subArea;
}

export function auditAreaPairSets(orcamentoRows: AreaPair[], realizadoRows: AreaPair[]): AreaAuditResult {
  let orcamentoSemPar = 0;
  let realizadoSemPar = 0;
  const orcPairs: AreaPair[] = [];
  const reaPairs: AreaPair[] = [];

  for (const p of orcamentoRows) {
    if (pairIsEmpty(p)) orcamentoSemPar += 1;
    else orcPairs.push(p);
  }
  for (const p of realizadoRows) {
    if (pairIsEmpty(p)) realizadoSemPar += 1;
    else reaPairs.push(p);
  }

  const orcCounts = countByKey(orcPairs);
  const reaCounts = countByKey(reaPairs);

  const orcExact = new Set(orcCounts.keys());
  const reaExact = new Set(reaCounts.keys());

  let bothExact = 0;
  for (const k of orcExact) {
    if (reaExact.has(k)) bothExact += 1;
  }

  const onlyOrcamento: AreaPairCount[] = [];
  for (const [k, count] of orcCounts) {
    if (!reaExact.has(k)) {
      const t = parseAreaExactKey(k);
      onlyOrcamento.push({ ...t, count });
    }
  }
  onlyOrcamento.sort((a, b) => b.count - a.count || a.area.localeCompare(b.area));

  const onlyRealizado: AreaPairCount[] = [];
  for (const [k, count] of reaCounts) {
    if (!orcExact.has(k)) {
      const t = parseAreaExactKey(k);
      onlyRealizado.push({ ...t, count });
    }
  }
  onlyRealizado.sort((a, b) => b.count - a.count || a.area.localeCompare(b.area));

  const byNorm = new Map<string, { orc: Map<string, number>; rea: Map<string, number> }>();
  const bump = (norm: string, side: "orc" | "rea", exact: string, n: number) => {
    let bucket = byNorm.get(norm);
    if (!bucket) {
      bucket = { orc: new Map(), rea: new Map() };
      byNorm.set(norm, bucket);
    }
    const sideMap = side === "orc" ? bucket.orc : bucket.rea;
    sideMap.set(exact, (sideMap.get(exact) ?? 0) + n);
  };

  for (const [k, n] of orcCounts) {
    bump(areaPairKeyNormalized(parseAreaExactKey(k)), "orc", k, n);
  }
  for (const [k, n] of reaCounts) {
    bump(areaPairKeyNormalized(parseAreaExactKey(k)), "rea", k, n);
  }

  const spellingDivergences: AreaSpellingDivergence[] = [];
  for (const [normKey, { orc, rea }] of byNorm) {
    if (orc.size === 0 || rea.size === 0) continue;
    let hasCommonExact = false;
    for (const ok of orc.keys()) {
      if (rea.has(ok)) {
        hasCommonExact = true;
        break;
      }
    }
    if (hasCommonExact) continue;
    const orcamentoVariants: AreaPairCount[] = [...orc.entries()]
      .map(([exactKey, count]) => ({ ...parseAreaExactKey(exactKey), count }))
      .sort((a, b) => b.count - a.count);
    const realizadoVariants: AreaPairCount[] = [...rea.entries()]
      .map(([exactKey, count]) => ({ ...parseAreaExactKey(exactKey), count }))
      .sort((a, b) => b.count - a.count);
    spellingDivergences.push({ normalizedKey: normKey, orcamentoVariants, realizadoVariants });
  }
  spellingDivergences.sort((a, b) => a.normalizedKey.localeCompare(b.normalizedKey));

  return {
    orcamentoDistinct: orcExact.size,
    realizadoDistinct: reaExact.size,
    bothExact,
    onlyOrcamento,
    onlyRealizado,
    spellingDivergences,
    orcamentoRowsAnalisadas: orcamentoRows.length,
    realizadoRowsAnalisadas: realizadoRows.length,
    orcamentoSemPar,
    realizadoSemPar,
  };
}
